import { MISSION_BY_ID } from '../data/missions';
import { canSwapMission } from './chipUtils';
import { evaluateHand } from './handEvaluator';
import { evaluateMissionById } from './missionEvaluator';
import { gameReducer } from './gameEngine';
import type { GameAction, GameState } from './types';
import { rightOf } from './types';

function heuristicMissionScore(missionId: string): number {
  const mission = MISSION_BY_ID[missionId];
  if (!mission) return 0;
  const base = mission.payout * 0.3;
  const difficultyBonus = (6 - mission.payout) * 0.1;
  return base + difficultyBonus;
}

function scoreMissionForBot(
  state: GameState,
  playerIndex: number,
  missionId: string,
): number {
  const mission = MISSION_BY_ID[missionId];
  if (!mission) return 0;

  const tray = state.players[playerIndex].tray;
  const simulatedBoard: (number | null)[] = [null, null, null, null, null];
  for (let i = 0; i < Math.min(5, tray.length); i += 1) {
    simulatedBoard[i] = tray[i];
  }

  const boards = state.players.map((p) => p.board);
  boards[playerIndex] = simulatedBoard;

  try {
    const success = evaluateMissionById(playerIndex, state.playerCount, boards, missionId);
    const base = success ? mission.payout * 2 : mission.payout * 0.3;
    const difficultyBonus = (6 - mission.payout) * 0.1;
    return base + difficultyBonus;
  } catch {
    return heuristicMissionScore(missionId);
  }
}

export function pickMissionDraftCard(state: GameState, playerIndex: number): string | null {
  const hand = state.missionDraftHands[playerIndex] ?? state.players[playerIndex].missionHand;
  if (hand.length === 0) return null;

  const scored = hand.map((id) => ({
    id,
    score: (MISSION_BY_ID[id]?.payout ?? 0) + Math.random() * 2,
  }));
  scored.sort((a, b) => b.score - a.score);
  return scored[Math.min(1, scored.length - 1)].id;
}

export function pickRoundMission(state: GameState, playerIndex: number): string | null {
  const hand = state.players[playerIndex].missionHand;
  if (hand.length === 0) return null;

  const scored = hand.map((id) => ({
    id,
    score: scoreMissionForBot(state, playerIndex, id),
  }));
  scored.sort((a, b) => b.score - a.score);
  return scored[0]?.id ?? hand[0];
}

export function pickBetAmount(state: GameState, playerIndex: number): number {
  const player = state.players[playerIndex];
  const missionId = player.selectedMissionId;
  if (!missionId || player.chips <= 0) return 1;

  const confidence = scoreMissionForBot(state, playerIndex, missionId);
  const ratio = confidence > 8 ? 0.5 : confidence > 5 ? 0.35 : 0.2;
  const amount = Math.max(1, Math.min(player.chips, Math.ceil(player.chips * ratio)));
  return amount;
}

export function shouldSwapMission(state: GameState, playerIndex: number): string | null {
  const player = state.players[playerIndex];
  if (player.missionSwappedThisRound) return null;
  if (!player.selectedMissionId) return null;
  if (!canSwapMission(player.betAmount, state.draftTurn)) return null;

  const currentScore = scoreMissionForBot(state, playerIndex, player.selectedMissionId);
  let bestAlt: string | null = null;
  let bestScore = currentScore;

  for (const id of player.missionHand) {
    if (id === player.selectedMissionId) continue;
    const score = scoreMissionForBot(state, playerIndex, id);
    if (score > bestScore + 1.5) {
      bestScore = score;
      bestAlt = id;
    }
  }

  return bestAlt;
}

export function pickDraftDieIndex(state: GameState, playerIndex: number): number {
  const tray = state.players[playerIndex].tray;
  if (tray.length === 0) return 0;

  const targetIndex = rightOf(playerIndex, state.playerCount);
  const targetBoard = state.players[targetIndex].board;
  const slot = state.draftTurn - 1;

  let bestIndex = 0;
  let bestScore = -Infinity;

  for (let i = 0; i < tray.length; i += 1) {
    const die = tray[i];
    const simulated = [...targetBoard];
    simulated[slot] = die;
    const filled = simulated.filter((d) => d !== null) as number[];
    const score = filled.length >= 3 ? 9 - evaluateHand([...filled, 1, 1].slice(0, 5)).rank : die;
    const sabotage = state.draftTurn <= 2 ? die : 6 - die;
    const total = score * 0.6 + sabotage * 0.4;
    if (total > bestScore) {
      bestScore = total;
      bestIndex = i;
    }
  }

  return bestIndex;
}

export function pickTurn4Dice(state: GameState, playerIndex: number): {
  toRightTrayIndex: number;
  keepTrayIndex: number;
} {
  const tray = state.players[playerIndex].tray;
  if (tray.length !== 2) {
    return { toRightTrayIndex: 0, keepTrayIndex: 1 };
  }

  const targetIndex = rightOf(playerIndex, state.playerCount);
  const targetBoard = [...state.players[targetIndex].board];
  const ownBoard = [...state.players[playerIndex].board];

  let best = { toRightTrayIndex: 0, keepTrayIndex: 1, score: -Infinity };

  for (let toRight = 0; toRight < 2; toRight += 1) {
    const keep = toRight === 0 ? 1 : 0;
    targetBoard[3] = tray[toRight];
    ownBoard[4] = tray[keep];
    const targetDice = targetBoard.filter((d) => d !== null) as number[];
    const ownDice = ownBoard.filter((d) => d !== null) as number[];
    const targetStrength = targetDice.length === 5 ? 9 - evaluateHand(targetDice).rank : 0;
    const ownStrength = ownDice.length === 5 ? 9 - evaluateHand(ownDice).rank : tray[keep];
    const score = ownStrength - targetStrength * 0.5;
    if (score > best.score) {
      best = { toRightTrayIndex: toRight, keepTrayIndex: keep, score };
    }
  }

  return { toRightTrayIndex: best.toRightTrayIndex, keepTrayIndex: best.keepTrayIndex };
}

export function createFallbackBotAction(state: GameState): GameAction | null {
  const actor = state.phase === 'settlement' ? state.settlementIndex : state.activePlayerIndex;
  const player = state.players[actor];
  if (!player || player.isHuman) return null;

  switch (state.phase) {
    case 'mission_draft': {
      const hand = state.missionDraftHands[actor] ?? player.missionHand;
      const id = hand[0];
      return id ? { type: 'MISSION_DRAFT_PICK', playerIndex: actor, missionId: id } : null;
    }
    case 'mission_select': {
      const id = player.missionHand[0];
      return id ? { type: 'SELECT_MISSION', playerIndex: actor, missionId: id } : null;
    }
    case 'betting':
      return { type: 'PLACE_BET', playerIndex: actor, amount: 1 };
    case 'settlement':
      return { type: 'SETTLEMENT_REVEAL', playerIndex: actor };
    default:
      return null;
  }
}

export function decideBotAction(state: GameState): GameAction | null {
  const actor = state.activePlayerIndex;
  const player = state.players[actor];
  if (!player || player.isHuman) return null;

  switch (state.phase) {
    case 'mission_draft': {
      const id = pickMissionDraftCard(state, actor);
      return id ? { type: 'MISSION_DRAFT_PICK', playerIndex: actor, missionId: id } : null;
    }
    case 'mission_select': {
      const id = pickRoundMission(state, actor);
      return id ? { type: 'SELECT_MISSION', playerIndex: actor, missionId: id } : null;
    }
    case 'betting': {
      const amount = pickBetAmount(state, actor);
      return { type: 'PLACE_BET', playerIndex: actor, amount };
    }
    case 'draft': {
      const swapId = shouldSwapMission(state, actor);
      if (swapId) {
        return { type: 'SWAP_MISSION', playerIndex: actor, newMissionId: swapId };
      }
      if (state.draftTurn === 4) {
        const pick = pickTurn4Dice(state, actor);
        return {
          type: 'DRAFT_TURN4',
          playerIndex: actor,
          toRightTrayIndex: pick.toRightTrayIndex,
          keepTrayIndex: pick.keepTrayIndex,
        };
      }
      const trayIndex = pickDraftDieIndex(state, actor);
      return { type: 'DRAFT_PLACE', playerIndex: actor, trayIndex };
    }
    case 'settlement':
      return { type: 'SETTLEMENT_REVEAL', playerIndex: state.settlementIndex };
    default:
      return null;
  }
}

export function runBotTurn(state: GameState): GameState {
  const action = decideBotAction(state);
  if (!action) return state;
  return gameReducer(state, action);
}
